import React, { useState, useEffect, useCallback } from 'react';
import { Button, TextInput, Label } from 'flowbite-react';
import { toast } from 'react-hot-toast';
import Swal from 'sweetalert2';
import { userApi } from '../../../api/user';
import { useAuth } from '../../../context/AuthContext';
import { ChevronDown, ChevronUp } from 'lucide-react';

const rolesData = [
  { id: 1, name: 'Admin' },
  { id: 2, name: 'Incorporator' },
  { id: 3, name: 'Sub Operator' },
  { id: 4, name: 'General Coordinator' },
  { id: 5, name: 'Coordinator' },
  { id: 6, name: 'Sub Coordinator' },
  { id: 7, name: 'Agent' },
  { id: 8, name: 'Sub Agent' },
  { id: 9, name: 'Player' },
];

export default function UserManagement({ userId, userData }) {
  const { tokens, user: currentUser } = useAuth();
  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedUpline, setSelectedUpline] = useState(null);
  const [currentUpline, setCurrentUpline] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [roleFilter, setRoleFilter] = useState(null);

  // Get the valid upline role (one level above the user being edited)
  const validUplineRoleId = userData?.user?.roleId > 1 ? userData.user.roleId - 1 : null;
  const validUplineRole = rolesData.find(r => r.id === validUplineRoleId);

  // Fetch current upline info if available
  useEffect(() => {
    if (userData?.user?.upline) {
      setCurrentUpline(userData.user.upline);
    }
  }, [userData]);

  // Debounced search
  const debounce = (func, wait) => {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func(...args), wait);
    };
  };

  const handleSearch = useCallback(
    debounce(async (value) => {
      if (!value) {
        setSearchResults([]);
        return;
      }
      setLoading(true);
      try {
        // Only search for users with the valid upline role
        const response = await userApi.getUsers(
          tokens.access.token,
          1,
          10,
          validUplineRoleId,
          value
        );
        if (response.status === 200) {
          setSearchResults(response.data.rows || []);
        } else {
          setSearchResults([]);
        }
      } catch (err) {
        setSearchResults([]);
      } finally {
        setLoading(false);
      }
    }, 500),
    [tokens.access.token, validUplineRoleId]
  );

  const onInputChange = (e) => {
    setSearch(e.target.value);
    handleSearch(e.target.value);
  };

  const handleAssignUpline = async (uplineUser) => {
    if (!uplineUser) return;
    const confirm = await Swal.fire({
      title: 'Assign New Upline?',
      text: `Are you sure you want to assign ${uplineUser.name} as the new upline?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes, assign',
    });
    if (!confirm.isConfirmed) return;
    try {
      setLoading(true);
      const response = await userApi.updateUpline(tokens.access.token, userId, uplineUser.id);
      if (response.status === 200) {
        setCurrentUpline(uplineUser);
        setSelectedUpline(null);
        setSearch('');
        setSearchResults([]);
        toast.success('Upline updated successfully!');
      } else {
        toast.error(response.data?.message || 'Failed to update upline');
      }
    } catch (err) {
      toast.error('Failed to update upline');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="py-6 px-2 max-w-xl mx-auto">
      <h2 className="text-lg font-semibold mb-4">User Management</h2>
      <div className="mb-6">
        <Label className="block mb-1">Current Upline</Label>
        {currentUpline ? (
          <div className="p-3 border rounded bg-gray-50 flex flex-col gap-1">
            <span className="font-medium">{currentUpline.name}</span>
            <span className="text-xs text-gray-500">{currentUpline.email}</span>
            <span className="text-xs text-gray-400">Role: {rolesData.find(r => r.id === currentUpline.roleId)?.name || currentUpline.roleId}</span>
          </div>
        ) : (
          <div className="text-sm text-gray-400">No upline assigned.</div>
        )}
      </div>
      <div className="mb-4">
        <Label className="block mb-1">Assign New Upline ({validUplineRole?.name || 'N/A'})</Label>
        <TextInput
          placeholder={`Search by name, email, or ID...`}
          value={search}
          onChange={onInputChange}
          disabled={!validUplineRoleId || loading}
        />
        {loading && <div className="text-xs text-gray-400 mt-1">Searching...</div>}
        {searchResults.length > 0 && (
          <div className="border rounded bg-white mt-2 max-h-56 overflow-y-auto shadow-md z-10 relative">
            {searchResults.map((u) => (
              <div
                key={u.id}
                className={`p-2 hover:bg-blue-50 cursor-pointer flex flex-col border-b last:border-b-0`}
                onClick={() => handleAssignUpline(u)}
              >
                <span className="font-medium">{u.name}</span>
                <span className="text-xs text-gray-500">{u.email}</span>
                <span className="text-xs text-gray-400">Role: {rolesData.find(r => r.id === u.roleId)?.name || u.roleId}</span>
              </div>
            ))}
          </div>
        )}
        {!loading && search && searchResults.length === 0 && (
          <div className="text-xs text-gray-400 mt-1">No users found.</div>
        )}
      </div>
    </div>
  );
}
